import type { AiSearchEvent } from '@/shared/api/graphql/aiSearch';
import { useTranslation } from '@/shared/lib/common/i18n';
import { Small } from '@/shared/ui/components/Typography';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Tooltip
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { linkForPage, linkForPatients, linkForResult } from '../lib/pageLinks';
import { extractCards } from '../lib/resultCards';
import type { AskPatient } from '../model/aiSearchStore';
import { eventsOf, turnPage, turnResult, turnStatus, useAiSearchStore } from '../model/aiSearchStore';
import { useAskAiSearch } from '../model/useAiSearch';
import AiSearchTurn from './AiSearchTurn';
import ViewOnPageLink from './ViewOnPageLink';

// "Chat more about this" is future work: hidden until the chat view is ready to ship.
const CHAT_MORE_ENABLED = false;

const PILL = { borderRadius: 999, px: 2.5, py: 0.75, textTransform: 'none', fontWeight: 600 } as const;

/**
 * Where to see it all: the page for the root fields the web maps (exact, from the query's own
 * arguments), else the page the agent named, else the patient list when the results are patients.
 */
const pageLinkOf = (events: AiSearchEvent[]) => {
  const result = turnResult(events);
  if (!result) return null;

  const agentPage = turnPage(events);
  const cards = () => (result.intent === 'query' ? extractCards(result.data, result.rootField).cards : []);
  return linkForResult(result.rootField, result.viewAllPayload) ?? linkForPage(agentPage?.id, agentPage?.tab) ?? linkForPatients(cards());
};

type ComposerProps = {
  onAsk: (question: string) => void;
  placeholder: string;
  disabled?: boolean;
  onCancel?: () => void;
};

/** The question box: "Ask another question" in an answer, "Ask anything" in a conversation. */
const Composer = ({ onAsk, placeholder, disabled = false, onCancel }: ComposerProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  return (
    <Paper
      component="form"
      variant="outlined"
      onSubmit={event => {
        event.preventDefault();
        if (!draft.trim() || disabled) return;
        onAsk(draft);
        setDraft('');
      }}
      sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, borderRadius: '28px', pl: 3, pr: 1, py: 0.5 }}
    >
      <InputBase
        value={draft}
        onChange={event => setDraft(event.target.value)}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder, maxLength: 500 }}
        autoFocus
        sx={{ flex: 1 }}
      />
      {onCancel && (
        <Button onClick={onCancel} sx={{ textTransform: 'none' }}>
          {t.aiSearch.panel.cancel}
        </Button>
      )}
      <IconButton type="submit" color="primary" aria-label={t.aiSearch.panel.send} disabled={!draft.trim() || disabled}>
        <ArrowUpwardIcon />
      </IconButton>
    </Paper>
  );
};

/** One question and its answer. The footer leads on to the full data, another question, or a chat. */
const AnswerBody = () => {
  const { t } = useTranslation();
  const ask = useAskAiSearch();
  const { turns, eventsBySearchId, startChat } = useAiSearchStore();
  const [asking, setAsking] = useState(false);

  const turn = turns[turns.length - 1];
  const events = turn ? eventsOf(turn, eventsBySearchId) : [];
  const status = turn && turnStatus(turn, events);

  const askNew = (question: string, patient?: AskPatient) => {
    setAsking(false);
    ask(question, patient);
  };

  return (
    <>
      <DialogContent dividers>{turn && <AiSearchTurn key={turn.id} turn={turn} events={events} onAsk={askNew} animate />}</DialogContent>

      {/* Nothing to lead on to until the search has finished. */}
      {status && status !== 'running' && (
        <DialogActions sx={{ px: 3, py: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          <Stack direction="row" useFlexGap spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {asking ? (
              <Composer onAsk={askNew} placeholder={t.aiSearch.panel.askAnother} onCancel={() => setAsking(false)} />
            ) : (
              <>
                <ViewOnPageLink link={pageLinkOf(events)} />
                <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setAsking(true)} sx={PILL}>
                  {t.aiSearch.panel.askAnother}
                </Button>
                {CHAT_MORE_ENABLED && (
                  <Button startIcon={<ChatBubbleOutlineIcon />} onClick={startChat} sx={{ ...PILL, ml: { sm: 'auto' } }}>
                    {t.aiSearch.panel.chatMore}
                  </Button>
                )}
              </>
            )}
          </Stack>
          <Small color="text.secondary" sx={{ ml: '0 !important' }}>
            {t.aiSearch.panel.disclaimer}
          </Small>
        </DialogActions>
      )}
    </>
  );
};

/** The whole conversation, full screen, with "Ask anything" below. */
const ChatBody = () => {
  const { t } = useTranslation();
  const ask = useAskAiSearch();
  const { turns, eventsBySearchId } = useAiSearchStore();
  const endRef = useRef<HTMLDivElement>(null);

  const busy = turns.some(turn => turnStatus(turn, eventsOf(turn, eventsBySearchId)) === 'running');

  // Keep the newest question and its progress in view.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [turns.length, eventsBySearchId]);

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={4} sx={{ maxWidth: 760, mx: 'auto', py: 2 }}>
          {turns.map((turn, index) => {
            const events = eventsOf(turn, eventsBySearchId);
            return (
              <Stack key={turn.id} spacing={1.5}>
                <AiSearchTurn turn={turn} events={events} onAsk={ask} animate={index === turns.length - 1} />
                {turnStatus(turn, events) !== 'running' && <ViewOnPageLink link={pageLinkOf(events)} />}
              </Stack>
            );
          })}
          <div ref={endRef} />
        </Stack>
      </DialogContent>

      <Box sx={{ px: 2, pb: 2, pt: 1.5 }}>
        <Stack sx={{ maxWidth: 760, mx: 'auto' }} spacing={1}>
          <Composer onAsk={ask} placeholder={t.aiSearch.panel.composerPlaceholder} disabled={busy} />
          <Small color="text.secondary" sx={{ textAlign: 'center' }}>
            {t.aiSearch.panel.chatNote} {t.aiSearch.panel.disclaimer}
          </Small>
        </Stack>
      </Box>
    </>
  );
};

/**
 * AI Search. Opens as a dialog with one answer; "Chat more about this" turns it into a full-screen
 * conversation that keeps the turns, until it is closed.
 */
const AiSearchPanel = () => {
  const { t } = useTranslation();
  const { open, mode, turns, setOpen } = useAiSearchStore();
  const chat = mode === 'chat';

  return (
    <Dialog
      open={open && turns.length > 0}
      onClose={() => setOpen(false)}
      fullScreen={chat}
      maxWidth="md"
      fullWidth
      aria-labelledby="ai-search-title"
      slotProps={{ paper: { sx: chat ? {} : { height: '80vh', borderRadius: 3 } } }}
    >
      <DialogTitle id="ai-search-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'primary.main' }}>
          <AutoAwesomeIcon fontSize="small" />
          <Small sx={{ fontWeight: 600, letterSpacing: 0.3 }}>{t.aiSearch.panel.title}</Small>
        </Stack>
        <Tooltip title={t.aiSearch.panel.close}>
          <IconButton aria-label={t.aiSearch.panel.close} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      {chat ? <ChatBody /> : <AnswerBody />}
    </Dialog>
  );
};

export default AiSearchPanel;
